import React, {Dispatch, FunctionComponent, SetStateAction, useCallback, useEffect, useLayoutEffect, useMemo, useState} from 'react';
import {
    Box,
    Breakpoint,
    Button,
    Card,
    CircularProgress,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Theme,
    Tooltip,
    Typography,
    Unstable_Grid2 as Grid,
    useMediaQuery
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DoneIcon from '@mui/icons-material/Done';
import EditIcon from '@mui/icons-material/Edit';
import FilterListIcon from '@mui/icons-material/FilterList';
import LayersRounded from '@mui/icons-material/LayersRounded';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';

import ManageDashboardsDialog from "./dialogs/manage-dashboards";
import CreateDashboardDialog from "./dialogs/create-dashboard";
import LoadingLottieAnimation from "../../../../../components/app-specific/lottie-animations/loading";
import DashboardWidget from './components/dashboard-widget';
import AddDashboardWidgetSection from './components/add-widget';
import DashboardViewFilterSection from './components/filter-section';
import axios, {CancelToken} from 'axios';
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import {
    AugmentedDashboardWidgetEntry,
    DashboardEntry,
    DashboardFilterParameter,
    DashboardGroupEntity,
    DashboardViewFilterSectionFilters,
    DashboardWidgetEntry,
    DashboardWidgetEntryInner,
    DashboardWidgetLayoutConfig,
    SavedUiComponentStateWidgetConfig
} from "./types";
import ShareDashboardDialog from "./dialogs/share-dashboard";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import Api from "../../../../../../core/services/api/api";
import {DashboardWidgetConfig} from "./dashboard-widget.config";
import {AddchartRounded, ContentCopyRounded, KeyboardArrowDownRounded, LockOpenRounded, PeopleRounded} from "@mui/icons-material";
import useQuery from '../../../../../hooks/use-query';
import Grid2 from '@mui/material/Unstable_Grid2';
import ToastService from "../../../../../../core/services/toast";
import DashboardViewLayout from "./components/layout";
import ManageDashboardLayoutDialog, {ManageDashboardLayoutDialogProps, ManageDashboardLayoutWidget} from "./dialogs/manage-dashboard-layout";
import useIndustry from "../../../../../hooks/use-industry";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";
import DashboardManipulationDialog, {DashboardManipulationVariant} from "./dialogs/dashboard-manipulation";
import ImportDashboardDialog from "./dialogs/import-dashboard";
import useSavedComponentsState from "../../../../../hooks/use-saved-components-state";
import useAccessLevels from "../../../../../hooks/use-access-levels";
import {flushSync} from "react-dom";
import {useTranslation} from "react-i18next";
import {NumberVisibilityModeProvider, useNumberVisibilityMode} from "./hooks/use-number-visibility-mode";
import NumberVisibilityModeToggle from "./components/number-visibility-mode-toggle";

/**
 * Initial state for the rearrange entries dialog
 */
const initialManageLayoutDialogState: ManageDashboardLayoutDialogProps = {
    open: false,
    close: () => undefined,
    title: '',
    widgets: [],
    onRearrange: async () => true,
};

const mobileThreshold: Breakpoint = 'md';
const alwaysMobile = true;

interface CachedDashboardWidgetsConfig {
    widgets: SavedUiComponentStateWidgetConfig[],
}

/**
 * Dashboard View Component
 *
 * A comprehensive dashboard management system that allows users to:
 * 1. Create, manage, and switch between multiple dashboards
 * 2. Add, remove, and rearrange widgets within dashboards
 * 3. Configure dashboard settings (default, filters, etc.)
 * 4. View and interact with widget data
 *
 * The component handles all dashboard-related operations including:
 * - Dashboard CRUD operations
 * - Widget management
 * - Layout customization
 * - Data visualization
 */
const DashboardV2View: FunctionComponent = () => {
    const [query, setQuery] = useQuery({
        parseOptions: {arrayFormat: 'bracket', parseBooleans: true, parseNumbers: false},
        stringifyOptions: {arrayFormat: 'bracket', parseBooleans: true, parseNumbers: false},
    } as any);
    const {cachedIndustry} = useIndustry();
    const {configureNotebookTags} = useConfigureNotebook('dashboard-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down(mobileThreshold)) || alwaysMobile;
    const isMounted = useIsMounted();
    const operatorAccessLevels = useAccessLevels();
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.dashboard-v2'});
    const {mode: numberVisibilityMode} = useNumberVisibilityMode();

    const [dashboards, setDashboards] = useState<DashboardEntry[]>([]);
    const [isLoadingDashboards, setIsLoadingDashboards] = useState(true);
    const [isInEditMode, setIsInEditMode] = useState(false);
    const [showFilterSection, setShowFilterSection] = useState(true);
    const [isAddComponentOpen, setIsAddComponentOpen] = useState(false);
    const [togglingDefaultDashboard, setTogglingDefaultDashboard] = useState(false);
    const [isDashboardManagementOpen, setIsDashboardManagementOpen] = useState(false);
    const [isShareDashboardOpen, setIsShareDashboardOpen] = useState(false);
    const [isCreateDashboardOpen, setIsCreateDashboardOpen] = useState(false);
    const [manipulationDialogState, setManipulationDialogState] = useState<{ open: boolean, variant: DashboardManipulationVariant, dashboard: DashboardEntry | null }>({
        open: false,
        variant: 'duplicate',
        dashboard: null
    });
    const [isImportDropzoneOpen, setIsImportDropzoneOpen] = useState(false);
    const [savingChanges, setSavingChanges] = useState(false);
    const [savingDashboardConfig, setSavingDashboardConfig] = useState(false);
    const [dashboardMenuAnchor, setDashboardMenuAnchor] = useState<null | HTMLElement>(null);
    const [manageLayoutDialogState, setManageLayoutDialogState] = useState<ManageDashboardLayoutDialogProps>(initialManageLayoutDialogState);
    const [savedWidgetsConfigs, setSavedWidgetsConfigs] = useState<SavedUiComponentStateWidgetConfig[]>([]);
    const [loadingConfig, setLoadingConfig] = useState(true);

    const loadingDashboards = useMemo(() => isLoadingDashboards || loadingConfig, [isLoadingDashboards, loadingConfig]);
    const selectedDashboardId = useMemo(() => query?.dashboardId ? Number(query?.dashboardId) : undefined, [query]);
    const selectedDashboard = useMemo<DashboardEntry | null>(() => dashboards.find(d => d.id === selectedDashboardId) ?? null, [dashboards, selectedDashboardId]);
    const dashboardAccess = operatorAccessLevels.packages.dashboard;
    const canOwnConfig = Boolean(dashboardAccess.viewDashboards && dashboardAccess.manageOwnDashboardConfigs);
    const canManageDashboards = Boolean(dashboardAccess.manageDashboards);
    const isSelectedDashboardOwned = selectedDashboard?.isOwnedByMe ?? false;
    const canManageSelectedDashboard = canManageDashboards && isSelectedDashboardOwned;
    const canViewWidgetFilters = Boolean(dashboardAccess.viewDashboardWidgetFilters || dashboardAccess.manageDashboardWidgetFilters);
    const canManageWidgetFilters = Boolean(dashboardAccess.manageDashboardWidgetFilters) && isSelectedDashboardOwned;
    const canExportWidgets = Boolean(dashboardAccess.exportDashboardWidgets);

    const {highestVersion: getCachedDashboardWidgetsConfig, save: saveCachedDashboardWidgetsConfig} = useSavedComponentsState<CachedDashboardWidgetsConfig>({
        name: `custom-dashboards-${selectedDashboardId}`,
        version: '2.0.0'
    });

    /**
     * Synchronizes the selected dashboard ID with the query parameters
     */
    const syncSelectedDashboardInQuery = useCallback((dashboardId: number | undefined) => {
        setQuery(({dashboardId: dashboardId}), {replace: true});
    }, [setQuery]);

    /**
     * Synchronizes the selected dashboard ID with the query parameters
     */
    const setDefaultDashboardAsSelectedDashboard = useCallback((dashboardId: number | undefined) => {
        setQuery(prevState => {
            if (!!prevState.dashboardId)
                return prevState;
            return ({dashboardId: dashboardId})
        }, {replace: true});
    }, [setQuery]);

    /**
     * When the dashboards finish loading, if there is a selected dashboard ID in the query but it doesn't exist in the loaded dashboards,
     * reset the selected dashboard to the default dashboard (or first dashboard if no default is set).
     */
    useEffect(() => {
        if (!loadingDashboards && !selectedDashboard && selectedDashboardId) {
            const defaultDashboardId = dashboards.find(d => d.isDefault)?.id ?? dashboards[0]?.id;
            if (defaultDashboardId) {
                syncSelectedDashboardInQuery(defaultDashboardId);
            }
        }
    }, [selectedDashboard, selectedDashboardId, loadingDashboards, dashboards, syncSelectedDashboardInQuery])

    /**
     * Sets the selected dashboard only if it exists.
     */
    const setSelectedDashboard = useCallback((state: SetStateAction<DashboardEntry>) => {
        setDashboards(prev => {
            const dashboard = prev?.find(d => d.id === selectedDashboardId);
            if (!dashboard) return prev;
            const newState = typeof state === 'function' ? state(dashboard) : state;
            return prev.map(d => d.id === selectedDashboardId ? {...d, ...newState} : d);
        });
    }, [selectedDashboardId]);

    const widgetsLayoutConfig = useMemo<Record<DashboardWidgetEntry['id'], DashboardWidgetLayoutConfig> | undefined>(() => {
        try {
            return JSON.parse(selectedDashboard?.uiComponentData ?? "");
        } catch {
            return undefined;
        }
    }, [selectedDashboard?.uiComponentData])

    const parameters = useMemo<DashboardFilterParameter[]>(() => {
        const params = new Set<DashboardFilterParameter>();
        for (const widget of (selectedDashboard?.newWidgets ?? [])) {
            if ((widget?.parameters?.length ?? 0) > 0) {
                for (const param of widget?.parameters ?? []) {
                    params.add(param.name);
                }
            }
        }
        // If category exists, ensure industry is included
        if (params.has(DashboardFilterParameter.PartCategories)) {
            params.add(DashboardFilterParameter.Industry);
        }
        return Array.from(params);
    }, [selectedDashboard?.newWidgets]);

    const cachedIndustryId = (cachedIndustry as any)?.id;

    const widgetFilters = useMemo<DashboardViewFilterSectionFilters>(() => {
        const newFilters = {...((query ?? {}) as any)};
        delete newFilters['dashboardId'];
        if (parameters.includes(DashboardFilterParameter.Industry) || parameters.includes(DashboardFilterParameter.PartCategories)) {
            newFilters.industry = newFilters.industry ?? cachedIndustryId;
        }
        return newFilters;
    }, [cachedIndustryId, parameters, query]);

    /**
     * Loads the filters from the server and sets them in the query state when the dashboard is selected.
     */
    const loadCachedWidgetsConfig = useCallback(async (selectedDashboardId: DashboardEntry['id'], cancelToken: CancelToken) => {
        const cachedDashboardConfig = await getCachedDashboardWidgetsConfig(cancelToken);
        if (!isMounted())
            return;
        let widgetsConfig: SavedUiComponentStateWidgetConfig[] = [];
        if (cachedDashboardConfig?.data) {
            widgetsConfig = cachedDashboardConfig?.data?.widgets ?? [];
        }
        setSavedWidgetsConfigs(widgetsConfig);
        setLoadingConfig(false);
    }, [getCachedDashboardWidgetsConfig, isMounted]);

    /**
     * Loads the widget configs when the selected dashboard changes
     */
    useEffect(() => {
        if (!selectedDashboardId) {
            setLoadingConfig(false);
            return;
        }
        const cancelTokenSource = axios.CancelToken.source();
        void loadCachedWidgetsConfig(selectedDashboardId, cancelTokenSource.token);
        return () => cancelTokenSource.cancel();
    }, [selectedDashboardId, loadCachedWidgetsConfig]);

    /**
     * Sets the widget filters based on the current query
     */
    const setFiltersOnQuery = useCallback<Dispatch<SetStateAction<DashboardViewFilterSectionFilters>>>((state) => {
        setQuery((prevState) => {
            const filters = typeof state === 'function' ? state(prevState) : state;
            delete filters['dashboardId'];
            return {dashboardId: prevState.dashboardId, ...filters};
        }, {replace: true})
    }, [setQuery]);

    const selectedDashboardWidgets = useMemo<AugmentedDashboardWidgetEntry[]>(() => {
        const widgets = selectedDashboard?.newWidgets
                ?.map<AugmentedDashboardWidgetEntry | null>(widget => {
                    const compatibleTypes = new DashboardWidgetConfig(widget.id).compatibleTypes;
                    if (!compatibleTypes) {
                        return null;
                    }
                    const layoutConfig = widgetsLayoutConfig?.[widget.connectionId];
                    const config = new DashboardWidgetConfig(widget.id, layoutConfig?.variant, layoutConfig?.type ?? compatibleTypes?.[0], numberVisibilityMode);
                    return {
                        ...widget,
                        variant: config.variant,
                        type: config.type!,
                        configuration: config.getDataConfig(widgetFilters, setFiltersOnQuery),
                        orderIndex: layoutConfig?.orderIndex,
                        size: layoutConfig?.size ?? config.sizeInformation?.size,
                        underlyingDataVisibility: layoutConfig?.underlyingDataVisibility,
                        orderBy: savedWidgetsConfigs.find(e => e?.connectionId === widget.connectionId)?.orderBy,
                        filters: widget.customFilter ? JSON.parse(widget.customFilter) as DashboardViewFilterSectionFilters : undefined,
                    } satisfies AugmentedDashboardWidgetEntry
                })
                ?.filter<AugmentedDashboardWidgetEntry>((e) => e !== null) as AugmentedDashboardWidgetEntry[]
            ?? [];
        widgets.sort((a, b) => {
            const orderA = a.orderIndex ?? Number.MAX_SAFE_INTEGER;
            const orderB = b.orderIndex ?? Number.MAX_SAFE_INTEGER;
            return orderA - orderB;
        });
        return widgets;
    }, [numberVisibilityMode, savedWidgetsConfigs, selectedDashboard?.newWidgets, setFiltersOnQuery, widgetFilters, widgetsLayoutConfig])

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.dashboard);
        configureBehavioralTool((tool) => tool.dashboard);
    }, [configureBehavioralTool, configureNotebookTags]);

    /**
     * Saves the given config as the dashboard ui data.
     */
    const saveDashboardUIData = useCallback(async (config) => {
        setSavingDashboardConfig(true);
        const response = await Api.updateCustomDashboardUIData({
            dashboardId: selectedDashboardId,
            uiComponentData: JSON.stringify(config),
        });
        if (!isMounted())
            return true;
        if (response?.resultFlag) {
            setSelectedDashboard(prev => ({
                ...prev,
                uiComponentData: JSON.stringify(config),
            }))
        }
        setSavingDashboardConfig(false)
        return response?.resultFlag ?? false;
    }, [isMounted, selectedDashboardId, setSelectedDashboard])

    /**
     * Saves the widget orders in the server
     * @type {(function(Record<string, int>): Promise<void>)}
     */
    const saveDashboardWidgetsLayoutConfig = useCallback(async (widgetsInOrder: ManageDashboardLayoutWidget[]) => {
        const config: Record<string, DashboardWidgetLayoutConfig> = Object.fromEntries(widgetsInOrder.map((e, index) =>
            [
                e.key,
                {
                    orderIndex: index,
                    size: e.size,
                    type: e.type,
                    underlyingDataVisibility: selectedDashboardWidgets?.find(w => w.connectionId === e.key)?.underlyingDataVisibility ?? {},
                    variant: selectedDashboardWidgets?.find(w => w.connectionId === e.key)?.variant,
                },
            ]
        ))
        return await saveDashboardUIData(config);
    }, [saveDashboardUIData, selectedDashboardWidgets])

    /**
     * Saves the widget configuration in the server
     */
    const saveDashboardWidgetConfig = useCallback(async (connectionId: DashboardWidgetEntry['id'], widgetConfig: DashboardWidgetLayoutConfig) => {
        const config: Record<string, DashboardWidgetLayoutConfig> = Object.fromEntries(selectedDashboardWidgets.map((e) => e.connectionId === connectionId
            ? [connectionId, widgetConfig]
            : [
                e.connectionId,
                {
                    orderIndex: e.orderIndex,
                    size: e.size,
                    type: e.type,
                    underlyingDataVisibility: e.underlyingDataVisibility,
                    variant: e.variant,
                }
            ]
        ))
        return await saveDashboardUIData(config);
    }, [saveDashboardUIData, selectedDashboardWidgets])


    /**
     * Saves the widget order by configuration in the server
     */
    const saveWidgetOrderBy = useCallback(async (data: SavedUiComponentStateWidgetConfig) => {
        const widgets: SavedUiComponentStateWidgetConfig[] = selectedDashboardWidgets.map((e) =>
            e.connectionId === data.connectionId
                ? {connectionId: data.connectionId, orderBy: data.orderBy}
                : {connectionId: e.connectionId, orderBy: e.orderBy}
        )
        const cachedDashboardSaveConfig = await getCachedDashboardWidgetsConfig();
        if (!isMounted())
            return;
        const mergedWidgets = cachedDashboardSaveConfig?.data?.widgets ?? [];
        for (const widget of widgets) {
            const existingWidget = mergedWidgets.find(e => e.connectionId === widget.connectionId);
            if (existingWidget) {
                existingWidget.orderBy = widget.orderBy;
            } else {
                mergedWidgets.push(widget);
            }
        }
        setSavedWidgetsConfigs(mergedWidgets);
        await saveCachedDashboardWidgetsConfig({widgets: mergedWidgets,});
    }, [getCachedDashboardWidgetsConfig, isMounted, saveCachedDashboardWidgetsConfig, selectedDashboardWidgets])

    /**
     * Saves the widget's Title.
     */
    const saveWidgetTitle = useCallback(async (connectionId: string, title?: string) => {
        setSelectedDashboard(prev => ({
            ...prev,
            newWidgets: prev.newWidgets.map(w => w.connectionId === connectionId ? {...w, customTitle: title ?? ""} : w),
        }));
    }, [setSelectedDashboard])

    /**
     * Saves the widget's Filters.
     */
    const saveWidgetFilters = useCallback(async (connectionId: string, filters?: DashboardViewFilterSectionFilters) => {
        setSelectedDashboard(prev => ({
            ...prev,
            newWidgets: prev.newWidgets.map(w => w.connectionId === connectionId ? {...w, customFilter: filters ? JSON.stringify(filters) : ""} : w),
        }));
    }, [setSelectedDashboard])


    /**
     * Fetches the list of available dashboards from the API
     * @param cancelToken - Optional axios cancel token for request cancellation
     */
    const fetchDashboards = useCallback(async (cancelToken?: any) => {
        setIsLoadingDashboards(true);
        const response = await Api.listCustomDashboards(cancelToken);
        if (!isMounted())
            return;
        if (!response?.aborted) {
            setIsLoadingDashboards(false);
        }
        if (response.resultFlag) {
            setDashboards(response.data?.map(e => ({
                ...e,
                newWidgets: e.newWidgets?.map(e => ({
                    ...e.widget,
                    connectionId: e.id,
                    customTitle: e.customTitle,
                    customFilter: e.customFilter
                }) satisfies DashboardWidgetEntry) ?? [],
            })) ?? []);
            const defaultDashboard = response.data.find(d => d.isDefault) ?? response.data[0];
            if (defaultDashboard) {
                setDefaultDashboardAsSelectedDashboard(defaultDashboard.id);
            }
        }
    }, [isMounted, setDefaultDashboardAsSelectedDashboard]);

    /**
     * As soon as the component mounts:
     * - fetch the list of dashboards
     */
    useEffect(() => {
        const cancelTokenSource = axios.CancelToken.source();
        void fetchDashboards(cancelTokenSource.token);
        return () => cancelTokenSource.cancel();
    }, [fetchDashboards]);

    /**
     * As soon as the selected dashboard changes:
     * - reset the edit mode and add component states
     * - reset the filter section visibility
     * - reset the widget filters
     */
    useLayoutEffect(() => {
        setIsInEditMode(false);
        setIsAddComponentOpen(false);
        setShowFilterSection(true);
    }, [selectedDashboardId]);

    /**
     * Sets or removes the default status of a dashboard
     * @param dashboardId - The ID of the dashboard to update
     */
    const setSelectedDashboardAsDefault = useCallback(async (e) => {
        e?.stopPropagation()
        e?.preventDefault()
        if (!selectedDashboard?.id)
            return;
        if (selectedDashboard?.isDefault) {
            ToastService.success({
                title: "Already Default",
                message: "This dashboard is already set as default. No changes are needed.",
            })
            return
        }
        setTogglingDefaultDashboard(true);
        const response = await Api.setDefaultCustomDashboard({
            dashboardId: selectedDashboard?.id
        });
        if (!isMounted())
            return;
        setTogglingDefaultDashboard(false);
        if (response.resultFlag) {
            setDashboards(prev => prev.map(d => ({...d, isDefault: d.id === selectedDashboardId ? !d.isDefault : false})));
        }
    }, [selectedDashboard?.id, selectedDashboard?.isDefault, isMounted, selectedDashboardId]);

    /**
     * Deletes a widget from the selected dashboard
     * @param widgetId - The ID of the widget to delete
     */
    const deleteDashboardWidget = useCallback(async (connectionId: DashboardWidgetEntry['connectionId']) => {
        await new Promise<void>(r => setSelectedDashboard(prev => {
            const newState = ({
                ...prev,
                newWidgets: prev.newWidgets.filter(w => w.connectionId !== connectionId),
            });
            r();
            return newState
        }));
    }, [setSelectedDashboard]);

    /**
     * Adds a new widget to the selected dashboard, optionally with widget-level filters.
     * @param widget - The widget template to add
     * @param filters - Optional widget-level filters to persist after add
     */
    const addDashboardWidget = useCallback(async (widget: DashboardWidgetEntryInner, filters?: DashboardViewFilterSectionFilters) => {
        setSavingChanges(true);
        const response = await Api.addCustomDashboardWidget({
            widgetId: widget.id,
            customTitle: widget.title,
            dashboardId: selectedDashboard?.id,
        });
        if (!isMounted())
            return false;
        if (!response?.resultFlag) {
            setSavingChanges(false);
            return false;
        }

        let customFilter = response.data?.customFilter as string | undefined;
        if (filters) {
            const updateResponse = await Api.updateCustomDashboardWidget({
                id: response.data?.id,
                customTitle: response.data?.title ?? widget.title,
                customFilter: JSON.stringify(filters),
            });
            if (!isMounted())
                return false;
            if (updateResponse?.resultFlag) {
                customFilter = JSON.stringify(filters);
            } else {
                ToastService.error({
                    title: t(t => t['add-widget'].saveFiltersFailed.title),
                    message: t(t => t['add-widget'].saveFiltersFailed.message),
                });
            }
        }

        setSavingChanges(false);
        await new Promise<void>(r => {
            setSelectedDashboard(prev => {
                const newState = ({
                    ...prev, newWidgets: [
                        ...prev.newWidgets,
                        {
                            ...widget,
                            connectionId: response.data?.id,
                            customTitle: response.data?.title,
                            customFilter,
                        }
                    ]
                });
                r();
                return newState;
            });
        })
        return true;
    }, [isMounted, selectedDashboard?.id, setSelectedDashboard, t]);

    /**
     * Opens the rearrange entries dialog with current widget configuration
     */
    const openRearrangeEntriesDialog = useCallback(() => {
        if (!selectedDashboard) return;
        setManageLayoutDialogState({
            open: true,
            close: () => setManageLayoutDialogState(initialManageLayoutDialogState),
            title: "Set Dashboard Layout",
            widgets: selectedDashboardWidgets.map(widget => {
                const config = new DashboardWidgetConfig(widget.id, widget.variant, widget.type);
                const sizing = config.sizeInformation;
                return ({
                    key: widget.connectionId,
                    title: widget.customTitle ?? widget.title,
                    size: widget.size ?? sizing?.size,
                    minSize: sizing?.minSize,
                    type: widget.type,
                })
            }),
            onRearrange: async (rearrangedWidgets) => {
                setManageLayoutDialogState(initialManageLayoutDialogState);
                return await saveDashboardWidgetsLayoutConfig(rearrangedWidgets);
            },
        });
    }, [saveDashboardWidgetsLayoutConfig, selectedDashboard, selectedDashboardWidgets]);

    /**
     * Opens the dashboard selection menu
     */
    const handleDashboardMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
        if (isInEditMode) {
            ToastService.warning({
                message: 'You cannot change the dashboard while in edit mode.',
                title: 'Warning',
            })
            return;
        }
        setDashboardMenuAnchor(event.currentTarget);
    }, [isInEditMode]);

    /**
     * Closes the dashboard selection menu
     */
    const handleDashboardMenuClose = useCallback(() => {
        setDashboardMenuAnchor(null);
    }, []);

    /**
     * Handles the selection of a dashboard from the menu
     * @param dashboard - The selected dashboard entry
     */
    const handleDashboardSelect = useCallback((dashboard: DashboardEntry) => {
        handleDashboardMenuClose();
        if (selectedDashboardId === dashboard.id) {
            return;
        }
        syncSelectedDashboardInQuery(dashboard.id);
        handleDashboardMenuClose();
    }, [handleDashboardMenuClose, selectedDashboardId, syncSelectedDashboardInQuery]);

    /**
     * Handles the completion of the dashboard manipulation process
     */
    const onManipulationDialogClose = useCallback((newDashboard?: DashboardEntry) => {
        setManipulationDialogState(prev => ({...prev, open: false}));
        if (!newDashboard) {
            return;
        }
        setDashboards(prev => {
            const exists = prev.some(d => d.id === newDashboard.id);
            const newList = exists ? prev.map(d => d.id === newDashboard.id ? newDashboard : d) : [...prev, newDashboard];
            return newList.map(d => ({...d, isDefault: newDashboard.isDefault ? (d.id === newDashboard.id) : d.isDefault}));
        });
        syncSelectedDashboardInQuery(newDashboard.id);
        if (isDashboardManagementOpen) {
            flushSync(() => {
                setIsDashboardManagementOpen(false);
            })
            setIsDashboardManagementOpen(true);
        }
    }, [isDashboardManagementOpen, syncSelectedDashboardInQuery])

    /**
     * Toggles the edit mode of the dashboard
     */
    const enterEditMode = useCallback(() => {
        setIsInEditMode(true);
    }, []);

    /**
     * Exits the edit mode of the dashboard
     */
    const exitEditMode = useCallback(() => {
        setIsInEditMode(false);
    }, [])

    /**
     * Closes the add component sidebar when not in edit mode
     */
    useLayoutEffect(() => {
        if (!isInEditMode) {
            setIsAddComponentOpen(false);
        }
    }, [isInEditMode])

    /**
     * Handles the opening of the share dashboard dialog
     */
    const openShareDashboardDialog = useCallback((e) => {
        e?.stopPropagation()
        e?.preventDefault()
        setIsShareDashboardOpen(true);
    }, []);

    /**
     * Handles the opening of the dashboard manipulation dialog
     */
    const openManipulationDialog = useCallback((variant: DashboardManipulationVariant, targetDashboard: DashboardEntry | null = selectedDashboard) => {
        setManipulationDialogState({open: true, variant, dashboard: targetDashboard});
    }, [selectedDashboard]);

    const openDuplicateDashboardDialog = useCallback((e) => {
        e?.stopPropagation()
        e?.preventDefault()
        openManipulationDialog('duplicate');
    }, [openManipulationDialog])

    /**
     * Handles the closing of the manage dashboards dialog
     * Updates the dashboards list and selected dashboard if needed
     */
    const handleManageDashboardsClose = useCallback((updatedDashboards?: Array<DashboardEntry>) => {
        setIsDashboardManagementOpen(false);
        if (updatedDashboards) {
            if (selectedDashboard?.id && !updatedDashboards.some(d => d.id === selectedDashboard.id)) {
                const defaultDashboard = updatedDashboards.find(d => d.isDefault) ?? updatedDashboards[0];
                syncSelectedDashboardInQuery(defaultDashboard?.id);
            }
            setDashboards(updatedDashboards);
        }
    }, [syncSelectedDashboardInQuery, selectedDashboard?.id]);

    /**
     * Opens the export dashboard dialog from the manage dashboards dialog
     */
    const openExportDashboardFromManage = useCallback((targetDashboard: DashboardEntry) => {
        openManipulationDialog('export', targetDashboard);
    }, [openManipulationDialog])

    /**
     * Opens the create dashboard dialog from the manage dashboards dialog
     */
    const openCreateDashboardFromManage = useCallback(() => {
        setIsCreateDashboardOpen(true);
    }, []);

    /**
     * Opens the import dashboard dialog from the manage dashboards dialog
     */
    const openImportDashboardFromManage = useCallback(() => {
        setIsImportDropzoneOpen(true);
    }, []);

    const openManipulationDialogFromImport = useCallback((parsedDashboard: DashboardEntry) => {
        setIsImportDropzoneOpen(false);
        openManipulationDialog('import', parsedDashboard);
    }, [openManipulationDialog])

    /**
     * Handles the closing of the create dashboard dialog
     * Adds the new dashboard to the list and selects it
     */
    const handleCreateDashboardClose = useCallback((dashboard?: DashboardEntry) => {
        setIsCreateDashboardOpen(false);
        if (dashboard) {
            setDashboards(prev => [...prev, dashboard]);
            syncSelectedDashboardInQuery(dashboard.id);
        }
        if (isDashboardManagementOpen) {
            flushSync(() => {
                setIsDashboardManagementOpen(false);
            })
            setIsDashboardManagementOpen(true);
        }
    }, [isDashboardManagementOpen, syncSelectedDashboardInQuery]);

    /**
     * Closes the share dashboard dialog
     * @param dashboard - The dashboard to close the dialog for
     */
    const closeShareDashboardDialog = useCallback((groupIds?: DashboardGroupEntity['id'][]) => {
        setIsShareDashboardOpen(false);
        setSelectedDashboard(prevState => ({...prevState, groupIds: groupIds ?? []}));
    }, [setSelectedDashboard])


    return (
        <>
            {
                loadingDashboards &&
                <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', marginBlock: 20}}>
                    <LoadingLottieAnimation className="loading-animation" width={'min(90%, 500px)'}/>
                </Box>
            }
            {
                !loadingDashboards && dashboards.length === 0 &&
                <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', marginBlock: 20}}>
                    <Card sx={{padding: 3, textAlign: 'center'}}>
                        <Typography variant="subtitle1" fontWeight={600} marginBlockEnd={4}>
                            Welcome to Your Dashboard
                        </Typography>
                        <Typography variant={'body2'} color="text.secondary" paragraph>
                            Get started by creating your first dashboard or import one from a JSON export.
                        </Typography>
                        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'center'}}>
                            {
                                canManageDashboards &&
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={openImportDashboardFromManage}
                                >
                                    Import Dashboard
                                </Button>
                            }
                            {
                                canManageDashboards &&
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<AddIcon/>}
                                    onClick={() => setIsCreateDashboardOpen(true)}
                                >
                                    Create Your First Dashboard
                                </Button>
                            }
                        </Box>
                    </Card>
                </Box>
            }
            {
                !loadingDashboards && dashboards.length > 0 &&
                <DashboardViewLayout
                    mobileThreshold={mobileThreshold}
                    alwaysMobile={alwaysMobile}
                    sidebarOpen={isInEditMode && canManageSelectedDashboard}
                    drawerOpen={isAddComponentOpen}
                    closeDrawer={() => setIsAddComponentOpen(false)}
                    sidebar={<AddDashboardWidgetSection onAdd={addDashboardWidget} mobileThreshold={mobileThreshold} alwaysMobile={alwaysMobile} selectedDashboardWidgets={selectedDashboardWidgets}/>}
                >
                    <Grid2 container spacing={1} sx={{mb: 3}} columns={12}>
                        <Grid2 xs={12} md={'auto'}>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                <Card
                                    onClick={handleDashboardMenuOpen}
                                    variant={'elevation'}
                                    sx={{
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        width: {xs: '100%', md: 'auto'},
                                        boxShadow: 'none',
                                        backgroundColor: 'secondary.light',
                                        borderRadius: 1,
                                        paddingInline: 2,
                                        paddingBlock: 1,
                                    }}
                                >
                                    <KeyboardArrowDownRounded/>
                                    <Typography variant={'subtitle2'} fontWeight={600} marginInlineEnd={'auto'}>
                                        {selectedDashboard?.title ?? dashboards?.find(e => e.id === selectedDashboardId)?.title ?? 'Select Dashboard'}
                                    </Typography>
                                    {
                                        !!selectedDashboard && canOwnConfig &&
                                        <Tooltip title={selectedDashboard?.isDefault ? "Remove this Dashboard as Default" : "Set this Dashboard as Default"}>
                                            <IconButton
                                                color={'secondary'}
                                                onClick={setSelectedDashboardAsDefault}
                                                disabled={savingChanges}
                                            >
                                                {
                                                    togglingDefaultDashboard
                                                        ? <CircularProgress size={20} color={'secondary'}/>
                                                        : selectedDashboard?.isDefault ? <StarIcon/> : <StarBorderIcon/>
                                                }
                                            </IconButton>
                                        </Tooltip>
                                    }
                                    {
                                        !!selectedDashboard && canManageSelectedDashboard &&
                                        <Tooltip title={"Manage Sharing Settings of this Dashboard"}>
                                            <Box component={'span'}>
                                                <IconButton color={'secondary'} onClick={openShareDashboardDialog} disabled={savingChanges}>
                                                    {
                                                        !selectedDashboard?.groupIds?.length
                                                            ? <LockOpenRounded fontSize={'small'}/>
                                                            : <PeopleRounded fontSize={'small'}/>
                                                    }
                                                </IconButton>
                                            </Box>
                                        </Tooltip>
                                    }
                                    {
                                        !!selectedDashboard && canManageDashboards &&
                                        <Tooltip title={"Duplicate this dashboard"}>
                                            <Box component={'span'}>
                                                <IconButton
                                                    onClick={openDuplicateDashboardDialog}
                                                    color={'secondary'}
                                                    disabled={savingChanges}
                                                >
                                                    <ContentCopyRounded/>
                                                </IconButton>
                                            </Box>
                                        </Tooltip>
                                    }
                                </Card>
                                <Menu anchorEl={dashboardMenuAnchor} open={Boolean(dashboardMenuAnchor)} onClose={handleDashboardMenuClose}>
                                    {
                                        dashboards.map((dashboard) =>
                                            <MenuItem
                                                key={dashboard.id}
                                                onClick={() => handleDashboardSelect(dashboard)}
                                                selected={selectedDashboardId === dashboard.id}
                                            >
                                                <ListItemText primary={dashboard.title} sx={{flexGrow: 1, marginInlineEnd: 'auto'}} primaryTypographyProps={{variant: 'body2', fontWeight: 600}}/>
                                                {
                                                    dashboard.isDefault &&
                                                    <ListItemIcon sx={{p: 0, display: 'flex', justifyContent: 'flex-end'}}>
                                                        <StarIcon color="secondary"/>
                                                    </ListItemIcon>
                                                }
                                            </MenuItem>
                                        )
                                    }
                                    <MenuItem
                                        color={'primary'}
                                        sx={{
                                            backgroundColor: 'primary.main',
                                            color: 'white',
                                            '&:hover': {backgroundColor: 'primary.dark'}
                                        }}
                                        onClick={() => {
                                            handleDashboardMenuClose();
                                            setIsDashboardManagementOpen(true);
                                        }}
                                    >
                                        <ListItemText primary="Manage Dashboards" primaryTypographyProps={{fontWeight: 600, variant: 'body2'}}/>
                                    </MenuItem>
                                </Menu>
                            </Box>
                        </Grid2>
                        <Grid2 xs={12} md={true}>
                            <Box sx={{display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'flex-end'}}>
                                <NumberVisibilityModeToggle/>
                                {
                                    parameters.length > 0 && !isInEditMode &&
                                    <Tooltip title={showFilterSection ? "Hide Filters" : "Show Filters"}>
                                        <Box component={'span'}>
                                            <IconButton
                                                onClick={() => setShowFilterSection(prev => !prev)}
                                                color={showFilterSection ? 'paper.primary' : 'default'}
                                                disabled={savingChanges}
                                            >
                                                <FilterListIcon/>
                                            </IconButton>
                                        </Box>
                                    </Tooltip>
                                }
                                {
                                    selectedDashboard && selectedDashboardWidgets.length > 0 && canOwnConfig &&
                                    <Button
                                        startIcon={<LayersRounded/>}
                                        onClick={openRearrangeEntriesDialog}
                                        disabled={savingChanges}
                                        variant="outlined"
                                        color="primary"
                                    >
                                        Manage Layout
                                    </Button>
                                }
                                {
                                    selectedDashboard && canManageSelectedDashboard && isInEditMode && isMobile &&
                                    <Button
                                        startIcon={<AddchartRounded/>}
                                        onClick={() => setIsAddComponentOpen(true)}
                                        variant="outlined"
                                        color="primary"
                                        disabled={savingChanges}
                                    >
                                        Add Widget
                                    </Button>
                                }
                                {
                                    canManageSelectedDashboard &&
                                    <Button
                                        startIcon={isInEditMode ? <DoneIcon/> : <EditIcon/>}
                                        onClick={() => !isInEditMode ? enterEditMode() : exitEditMode()}
                                        disabled={savingChanges}
                                        variant="outlined"
                                        color={'primary'}
                                    >
                                        {
                                            savingChanges
                                                ? "Saving Changes..."
                                                : isInEditMode
                                                    ? 'Done'
                                                    : 'Edit Dashboard'
                                        }
                                    </Button>
                                }
                            </Box>
                        </Grid2>
                    </Grid2>
                    {
                        !!selectedDashboard &&
                        <>
                            <DashboardViewFilterSection
                                visible={showFilterSection && !isInEditMode && parameters.length > 0}
                                canSaveFilters={canOwnConfig}
                                filters={widgetFilters}
                                setFilters={setFiltersOnQuery}
                                parameters={parameters}
                                dashboardId={selectedDashboardId}
                            />
                            <Grid container spacing={2} marginBlockStart={6}>
                                {selectedDashboardWidgets.length === 0 &&
                                    <Grid xs={12}>
                                        <Card sx={{p: 4, textAlign: 'center', backgroundColor: 'transparent'}}>
                                            <Typography variant="subtitle1" gutterBottom>
                                                No Widgets Added
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" paragraph>
                                                Add widgets to your dashboard to visualize your data.
                                            </Typography>
                                        </Card>
                                    </Grid>
                                }
                                {
                                    selectedDashboardWidgets.length > 0 &&
                                    selectedDashboardWidgets.map((widget) => (
                                        !!widget &&
                                        <Grid key={widget.connectionId} {...widget?.size}>
                                            <DashboardWidget
                                                widget={widget}
                                                dashboardId={selectedDashboardId!}
                                                filters={widgetFilters}
                                                isEditMode={isInEditMode}
                                                onDelete={deleteDashboardWidget}
                                                savingDashboardWidgetConfig={savingChanges || savingDashboardConfig}
                                                saveDashboardWidgetConfig={canOwnConfig ? saveDashboardWidgetConfig : undefined}
                                                saveWidgetOrderBy={canOwnConfig ? saveWidgetOrderBy : undefined}
                                                onTitleUpdated={saveWidgetTitle}
                                                onFilterUpdated={saveWidgetFilters}
                                                canViewWidgetFilters={canViewWidgetFilters}
                                                canManageWidgetFilters={canManageWidgetFilters}
                                                canExportWidgets={canExportWidgets}
                                            />
                                        </Grid>
                                    ))
                                }
                            </Grid>
                        </>
                    }
                </DashboardViewLayout>
            }
            <CreateDashboardDialog
                open={isCreateDashboardOpen}
                onClose={handleCreateDashboardClose}
            />
            <ManageDashboardLayoutDialog
                open={manageLayoutDialogState.open}
                close={manageLayoutDialogState.close}
                title={manageLayoutDialogState.title}
                widgets={manageLayoutDialogState.widgets}
                onRearrange={manageLayoutDialogState.onRearrange}
            />
            <ManageDashboardsDialog
                open={isDashboardManagementOpen}
                onClose={handleManageDashboardsClose}
                onExportDashboard={openExportDashboardFromManage}
                onCreateDashboard={openCreateDashboardFromManage}
                onImportDashboard={openImportDashboardFromManage}
            />
            <ShareDashboardDialog
                open={isShareDashboardOpen}
                dashboard={selectedDashboard}
                onClose={closeShareDashboardDialog}
            />
            <DashboardManipulationDialog
                open={manipulationDialogState.open}
                variant={manipulationDialogState.variant}
                onClose={onManipulationDialogClose}
                dashboard={manipulationDialogState.dashboard}
            />
            <ImportDashboardDialog
                open={isImportDropzoneOpen}
                onClose={() => setIsImportDropzoneOpen(false)}
                onParsed={openManipulationDialogFromImport}
            />
        </>
    );
};

const DashboardV2ViewWithNumberVisibility: FunctionComponent = () => (
    <NumberVisibilityModeProvider>
        <DashboardV2View/>
    </NumberVisibilityModeProvider>
);

export default DashboardV2ViewWithNumberVisibility;
export {DashboardFilterParameter} from "./types";
export type {DashboardViewFilterSectionFilters} from "./types";